// DOC-DEPS: LLM.md -> docs/MAGIC_PEN_CAPTURE_SPEC.md -> src/features/chat/README.md -> src/features/growth/GrowthPage.tsx
import { callMagicPenParseAPI } from '../../api/client';
import { buildDraftsFromAIResult, validateDrafts } from './magicPenDraftBuilder';
import { parseMagicPenInputLocal } from './magicPenParserLocalFallback';
import { salvageTodoDraftFromUnparsedSegment } from './magicPenTodoSalvage';
import type { MagicPenParseResult } from './magicPenTypes';

interface ParseMagicPenOptions {
  now?: Date;
  lang?: 'zh' | 'en' | 'it';
}

function detectMagicPenLang(rawText: string, requestedLang: 'zh' | 'en' | 'it'): 'zh' | 'en' | 'it' {
  if (/[\u3400-\u9fff]/.test(rawText)) return 'zh';
  return requestedLang;
}


function classifyParseOutcome(response: {
  parseStrategy?: 'direct_json' | 'wrapped_object' | 'fallback_failed';
  providerUsed?: 'deepseek' | 'none';
  failureCategory?: 'model_output_invalid' | 'provider_call_failed' | 'unknown';
  data: { unparsed: string[] };
}): 'ok' | 'partial_unrecognized' | 'parse_failed' {
  if (response.parseStrategy === 'fallback_failed' || response.providerUsed === 'none') {
    return 'parse_failed';
  }
  if (response.data.unparsed.length > 0) {
    return 'partial_unrecognized';
  }
  return 'ok';
}

function preprocessRawText(rawText: string): string {
  return rawText.trim().replace(/[^\S\n]+/g, ' ');
}

function recoverLocally(
  rawText: string,
  now: Date,
  lang: 'zh' | 'en' | 'it',
): MagicPenParseResult {
  const fallback = parseMagicPenInputLocal(rawText, now, lang);
  const hasRecognizedItems = fallback.drafts.length > 0 || fallback.autoWriteItems.length > 0;
  if (hasRecognizedItems || fallback.unparsedSegments.length > 0) {
    return fallback;
  }
  return { drafts: [], unparsedSegments: [rawText], autoWriteItems: [] };
}

function toLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toLocalDateTimeString(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  const hour = `${date.getHours()}`.padStart(2, '0');
  const minute = `${date.getMinutes()}`.padStart(2, '0');
  return `${year}-${month}-${day} ${hour}:${minute}`;
}

export async function parseMagicPenInput(
  rawText: string,
  options: ParseMagicPenOptions = {},
): Promise<MagicPenParseResult> {
  const cleaned = preprocessRawText(rawText);
  if (!cleaned) {
    return { drafts: [], unparsedSegments: [], autoWriteItems: [] };
  }

  const now = options.now ?? new Date();
  const effectiveLang = detectMagicPenLang(cleaned, options.lang ?? 'zh');

  try {
    const response = await callMagicPenParseAPI({
      rawText: cleaned,
      lang: effectiveLang,
      todayDateStr: toLocalDateString(now),
      currentHour: now.getHours(),
      currentLocalDateTime: toLocalDateTimeString(now),
      timezoneOffsetMinutes: -now.getTimezoneOffset(),
    });
    const parseOutcome = classifyParseOutcome(response);

    if (parseOutcome === 'parse_failed') {
      return recoverLocally(cleaned, now, effectiveLang);
    }

    const built = buildDraftsFromAIResult(response.data, now, effectiveLang);
    const salvagedDrafts = effectiveLang === 'zh'
      ? built.unparsedSegments
        .map((segment) => salvageTodoDraftFromUnparsedSegment(segment, now))
        .filter((draft): draft is NonNullable<typeof draft> => !!draft)
      : [];
    const salvagedSourceSet = new Set(salvagedDrafts.map((draft) => draft.sourceText.trim()));
    const remainingUnparsed = built.unparsedSegments.filter((segment) => !salvagedSourceSet.has(segment.trim()));
    const result = {
      drafts: validateDrafts([...built.drafts, ...salvagedDrafts], [], now.getTime()),
      unparsedSegments: remainingUnparsed,
      autoWriteItems: built.autoWriteItems,
    };
    if (result.drafts.length === 0
      && result.unparsedSegments.length === 0
      && result.autoWriteItems.length === 0) {
      return recoverLocally(cleaned, now, effectiveLang);
    }
    return result;
  } catch (error) {
    void error;
    return recoverLocally(cleaned, now, effectiveLang);
  }
}
